import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import { type ContactBankAccountSchema, contactBankAccountSchema } from "@msuite/utils/schemas";
import { FormProvider, useForm } from "react-hook-form";
import { ContactBankAccountForm } from "@/forms/contact-bank-account-form";
import { client } from "@/hono";

/** Props Interface */
interface EditContactBankAccountModalProps extends ModalProps {
	contactId: string;
	bankAccountId: string;
}

export function EditContactBankAccountModal({
	isOpen,
	onClose,
	contactId,
	bankAccountId,
}: EditContactBankAccountModalProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const methods = useForm<ContactBankAccountSchema>({
		resolver: resolver(contactBankAccountSchema),
		defaultValues: async () => {
			const response = await client.contacts[":contactId"]["bank-accounts"][":bankAccountId"].$get({
				param: { contactId, bankAccountId },
			});
			const { bankAccount } = await responseHandler(response);
			return contactBankAccountSchema.parse(bankAccount);
		},
	});

	/** Functions */
	async function handleOnSubmit(json: ContactBankAccountSchema) {
		const response = await client.contacts[":contactId"]["bank-accounts"][":bankAccountId"].$patch({
			param: { contactId, bankAccountId },
			json,
		});
		await responseHandler(response);
		await qc.invalidateQueries({ predicate: invalidate(["contact", "incoming-invoices"]) });
		onClose();
	}

	/** Render */
	return (
		<FormProvider {...methods}>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				title={t("contacts.bank_accounts.edit")}
				onSubmit={methods.handleSubmit(handleOnSubmit)}
				isLoading={methods.formState.isLoading}
			>
				<Modal.Body>
					<ContactBankAccountForm />
				</Modal.Body>
				<Modal.FooterForm />
			</Modal>
		</FormProvider>
	);
}
